import { captureMessage } from "@/error/sentry";
import { useActivityStore } from "@/store/activity";
import { useBoardStore } from "@/store/board";
import { useDrawStore } from "@/store/draw";
import { usePanModeStore } from "@/store/panMode";
import { useUserStore } from "@/store/user";

import type { BackendWriter } from "./BackendWriter";

// checks if the writer is used by a user without any permissions.
// this is more debugging than permissions checking.
// all code should check the necessary permissions before using the writer.
// ignoring calls can cause follow-up errors.
export function permissionChecked(
  writer: BackendWriter,
  options: { allowed: string[]; ignored: string[] },
) {
  const wrappers = new Map<string | symbol, (...args: unknown[]) => unknown>();

  return new Proxy(writer, {
    get(target, p, receiver) {
      const value = Reflect.get(target, p, receiver);
      if (typeof value !== "function" || options.allowed.includes(p as string)) {
        return value;
      }
      let wrapped = wrappers.get(p);
      if (!wrapped) {
        wrapped = (...args: unknown[]) => {
          if (useUserStore().hasNoPermissions) {
            if (options.ignored.includes(p as string)) {
              return;
            }
            captureMessage(`Called BackendWriter.${String(p)} without permission`, {
              info: {
                visualize: useActivityStore().tab === "visualize",
                panMode: usePanModeStore().active,
                role: useUserStore().technicalUser.role,
                board: useBoardStore().board?.type,
                drawMode: useDrawStore().active,
              },
            });
          }
          return value.apply(target, args);
        };
        wrappers.set(p, wrapped);
      }
      return wrapped;
    },
  });
}
